import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosInstance } from 'axios';
import { z } from 'zod/v4';
import { type ApiError, type ApiResponse, useLegacyAxiosInstance } from './utils';
import { addActionNameToMutationResult } from './utils/add-action-name-to-mutation-result';

const closeThirdPartyCaseVariablesSchema = z.object({
  id: z.number().or(z.string().transform(Number)),
});

export type CloseThirdPartyCaseVariables = z.infer<typeof closeThirdPartyCaseVariablesSchema>;
export async function closeThirdPartyCaseFn(
  axiosInstance: Promise<AxiosInstance>,
  data: CloseThirdPartyCaseVariables,
) {
  const instance = await axiosInstance;
  return instance.post(`/thirdparty_cases/${data.id}/close`);
}

export function useCloseThirdPartyCase() {
  const client = useQueryClient();
  const axiosInstance = useLegacyAxiosInstance();

  const mutation = useMutation<ApiResponse, ApiError, CloseThirdPartyCaseVariables>({
    mutationFn: variables => closeThirdPartyCaseFn(axiosInstance, variables),
    onSuccess: (_, variables) => {
      client.invalidateQueries({ queryKey: ['thirdparty_cases'] });
      client.invalidateQueries({ queryKey: ['third-party-case', variables.id] });
    },
  });

  return addActionNameToMutationResult<
    ApiResponse,
    ApiError,
    CloseThirdPartyCaseVariables,
    'closeThirdPartyCase'
  >('closeThirdPartyCase', mutation);
}
